import { useMemo, useState } from 'react';

import { type FieldValues, type UseControllerProps, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Input, type InputProps } from '@wf/components';

import { type LicenseFieldProps } from './LicenseFieldController';

import { useLicenseController } from '../../hooks/useLicenseController';

import { defaultScaleSelector } from '@/store/configs/selectors';
import formatNumber from '../../../../helpers/formatNumber';

export interface ITextFieldProps extends InputProps {
  regexp?: RegExp;
  formatAsNumber?: boolean;
  fractionDigits?: number;
  triggerOnChange?: boolean;
}

export const LicenseTextField = ({
  name,
  defaultValue,
  regexp,
  configName,
  featureFlag,
  viewMode,
  rules,
  shouldUnregister,
  control,
  formatAsNumber,
  fractionDigits,
  triggerOnChange,
  disabled,
  ...rest
}: ITextFieldProps & LicenseFieldProps & UseControllerProps<FieldValues, string>) => {
  const { control: contextControl, trigger } = useFormContext();
  const {
    i18n: { language },
  } = useTranslation();

  const [focused, setFocused] = useState(false);

  const defaultScale = useSelector(defaultScaleSelector) ?? 2;

  const {
    field: { value, onChange, onBlur, ...fieldRest },
    computedProps,
    config,
  } = useLicenseController({
    name,
    configName,
    featureFlag,
    control: control ?? contextControl,
    defaultValue: defaultValue ?? '',
    rules,
    shouldUnregister,
    viewMode,
    disabled,
  });

  const formattedValue = useMemo(() => {
    if (!formatAsNumber || focused || (!value && value !== 0)) {
      return value ?? '';
    }

    return formatNumber({
      value,
      locale: language,
      maximumFractionDigits: fractionDigits ?? defaultScale,
      minimumFractionDigits: fractionDigits ?? defaultScale,
    });
  }, [focused, formatAsNumber, fractionDigits, language, value, defaultScale]);

  const handleChange: typeof rest.onChange = (e) => {
    if (regexp && !regexp.test(e.target.value) && e.target.value !== '') {
      return;
    }

    onChange(e);

    if ('onChange' in rest && typeof rest.onChange === 'function') {
      rest.onChange(e);
    }

    if (triggerOnChange) {
      void trigger(name);
    }
  };

  const handleFocus: typeof rest.onFocus = (...args) => {
    if ('onFocus' in rest && typeof rest.onFocus === 'function') {
      rest.onFocus(...args);
    }

    setFocused(true);
  };

  const handleBlur: typeof rest.onBlur = (...args) => {
    onBlur();
    setFocused(false);

    if ('onBlur' in rest && typeof rest.onBlur === 'function') {
      rest.onBlur(...args);
    }
  };

  if (config.isHidden) {
    return <></>;
  }

  return (
    <Input
      {...fieldRest}
      {...computedProps}
      value={formattedValue}
      {...rest}
      valueView={formattedValue || '-'}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      autoComplete="off"
    />
  );
};
